import { t } from 'i18next'
import { useNavigate } from 'react-router'

import { AlertDialog } from '@/components/alert-dialog'
import { useGetSimulacaoDuplicar } from '@/features/simulador/brasil/hooks'
import { useSimulacoesRelacionadas } from '@/features/simulador/brasil/hooks/use-simulacoes-relacionadas'
import { useValidarSimulacaoParaProposta } from '@/features/simulador/brasil/hooks/use-validar-simulacao-para-proposta'
import type { TValidarSimulacaoParaPropostaResponse } from '@/features/simulador/shared/http/validar-simulacao-para-proposta.service'
import { useGuardedHandler } from '@/hooks/use-guarded-handlers'
import { showToast } from '@/infra/sonner'
import type { TGetSimulacaoResponse } from '@/types/simulacao'
import { checkOnlineGuard } from '@/utils'
import { storageKeys } from '@/utils/constants'

interface UseValidarLancarPropostaParams {
  simulacaoId: string
  nomeEmpreendimento?: string
  handleIniciarAtendimento?: () => Promise<void>
  tipoDoProduto: TGetSimulacaoResponse['produto']['tipoDoProduto']
}

const handleParcelaVencida = async () => {
  return await AlertDialog.show({
    title: t('simulador:modais.parcela_vencida.titulo'),
    description: t('simulador:modais.parcela_vencida.descricao'),
    actions: [
      { title: t('simulador:modais.parcela_vencida.cancelar'), value: 'cancel', variant: 'outline' },
      { title: t('simulador:modais.parcela_vencida.confirmar'), value: 'confirm', variant: 'default' },
    ],
  })
}

const handleValidationError = async (
  errors: Extract<TValidarSimulacaoParaPropostaResponse, { result: false }>['errors'],
): Promise<'confirm' | 'cancel' | 'dismiss' | undefined> => {
  const error = errors.find((e) =>
    [
      'PRODUCT_UNAVAILABLE',
      'SIMULATION_ACCESSORY_ZEROED',
      'SIMULATION_ALREADY_USED_BY_ANOTHER_PROPOSAL',
      'EXPIRED_SIMULATION',
      'OVERDUE_INSTALLMENT',
    ].includes(e.code),
  )

  if (!error) return

  const errorMessages = {
    PRODUCT_UNAVAILABLE: 'simulador:simulador.errors.validacao_proposta.produto_indisponivel',
    SIMULATION_ACCESSORY_ZEROED: 'simulador:simulador.errors.validacao_proposta.acessorio_zerado',
    SIMULATION_ALREADY_USED_BY_ANOTHER_PROPOSAL: 'simulador:simulador.errors.validacao_proposta.simulacao_utilizada',
    EXPIRED_SIMULATION: 'simulador:simulador.errors.validacao_proposta.simulacao_vencida',
  } as const

  if (error.code === 'OVERDUE_INSTALLMENT') {
    const result = await handleParcelaVencida()
    return result.value
  }

  const messageKey = errorMessages[error.code]
  if (messageKey) {
    showToast({
      message: t(messageKey),
      variant: 'error',
    })
  }

  return
}

export const useValidarLancarProposta = ({
  simulacaoId,
  nomeEmpreendimento,
  tipoDoProduto,
  handleIniciarAtendimento,
}: UseValidarLancarPropostaParams) => {
  const navigate = useNavigate()

  const { refetch: validarInformacoesSimulacaoParaLancarProposta } = useValidarSimulacaoParaProposta(simulacaoId)
  const { refetch: querySimulacoesRelacionadas } = useSimulacoesRelacionadas(simulacaoId)
  const { mutateAsync: getSimulacaoDuplicar } = useGetSimulacaoDuplicar()

  const handleNavegacaoProposta = async () => {
    const response = await validarInformacoesSimulacaoParaLancarProposta()

    if (response.data?.result === false && response.data.errors[0]) {
      const validatedResponse = await handleValidationError(response.data.errors)
      if (validatedResponse === 'confirm') {
        const simulacaoDuplicada = await getSimulacaoDuplicar(simulacaoId)
        sessionStorage.removeItem(storageKeys.simulador_form)
        navigate('/simulador', {
          state: {
            simulacaoDuplicada,
            empreendimento: simulacaoDuplicada.empreendimento,
            idExternoProduto: simulacaoDuplicada.produto.idExterno,
            idExternoBloco: simulacaoDuplicada.bloco.idExterno,
            tipoDoProduto: simulacaoDuplicada.produto.tipoDoProduto,
          },
        })
      }

      return
    }
    if (response.isError) {
      showToast({
        message: t('common:errors_gerais.padrao'),
        variant: 'error',
      })
      return
    }

    await handleIniciarAtendimento?.()
    try {
      if (tipoDoProduto === 'Lote') return navigate('/proposta', { state: { simulacoesIds: [simulacaoId] } })
      const { data } = await querySimulacoesRelacionadas()
      if (!data?.relacionadas.length) return navigate('/proposta', { state: { simulacoesIds: [simulacaoId] } })
      return navigate('/proposta/selecao-produtos', { state: { simulacoes: data, nomeEmpreendimento } })
    } catch {
      navigate('/proposta', { state: { simulacoesIds: [simulacaoId] } })
    }
  }

  const handleValidarLancarProposta = useGuardedHandler([checkOnlineGuard()], handleNavegacaoProposta)

  return {
    handleValidarLancarProposta,
  }
}
